import React from 'react';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { surveyService } from '../../services/survey.service';
import { Button, Badge } from '../ui';
import LoadingSpinner from '../ui/LoadingSpinner';

const typeLabels: Record<string, string> = {
  boolean: 'Sim/Não',
  text: 'Texto',
  single_choice: 'Escolha única',
  multiple_choice: 'Múltipla escolha',
};

interface ResponseDetailProps {
  responseId: number;
  onClose: () => void;
}

export const ResponseDetail: React.FC<ResponseDetailProps> = ({ responseId, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['survey-response-detail', responseId],
    queryFn: () => surveyService.getResponse(responseId),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!data) return null;

  const formatAnswer = (answer: typeof data.answers[0]) => {
    if (answer.answer_boolean !== null && answer.answer_boolean !== undefined) {
      return answer.answer_boolean ? 'Sim' : 'Não';
    }
    if (answer.answer_text) return answer.answer_text;
    if (answer.option_text) return answer.option_text;
    return '-';
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm">
        <div>
          <span className="text-gray-500">ID da Resposta:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{data.id}</span>
        </div>
        {data.ticket_id && (
          <div>
            <span className="text-gray-500">Ticket:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{data.ticket_id}</span>
          </div>
        )}
        <div>
          <span className="text-gray-500">Usuário:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{data.user_id}</span>
        </div>
        <div>
          <span className="text-gray-500">Data:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
            {format(new Date(data.created_at), 'dd/MM/yyyy HH:mm')}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="font-medium text-gray-900 dark:text-gray-100">Respostas</h4>
        {data.answers.map((answer, idx) => (
          <div
            key={answer.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {idx + 1}. {answer.question_text}
              </p>
              <Badge variant="default" className="flex-shrink-0 text-xs">
                {typeLabels[answer.question_type] || answer.question_type}
              </Badge>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 px-3 py-2 rounded">
              {formatAnswer(answer)}
            </p>
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-2">
        <Button variant="secondary" onClick={onClose}>Fechar</Button>
      </div>
    </div>
  );
};
